import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import MapSearchBox from './MapSearchBox';
import MapOverlayInfo from './MapOverlayInfo';
import DrawToolButton from './DrawToolButton';
import GOOGLE_MAP_ENUMS from '../../../shared/engage/engage-google-map-enums';
import MapUtils from './MapUtils';

const google = window.google;

const shapeTypes = GOOGLE_MAP_ENUMS().shapeTypes;
const mapOptions = GOOGLE_MAP_ENUMS().mapOptions;

// Labels for the draw-tool control bar (Terra Draw, unlike the old DrawingManager, ships no UI).
const DRAW_TOOL_LABELS = {
  [shapeTypes.MARKER]: 'Marker',
  [shapeTypes.CIRCLE]: 'Circle',
  [shapeTypes.RECTANGLE]: 'Rectangle',
  [shapeTypes.POLYGON]: 'Polygon',
};

const DRAW_TOOLS_STYLE = {
  position: 'absolute',
  bottom: 12,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 8,
  zIndex: 5,
};

function MapComponent(props) {
  const {
    id,
    label,
    mapData,
    setMapData,
    mapZoom,
    setMapZoom,
    mapType,
    setMapType,
    mapDescription,
    clearMapText,
    allowedDrawTools,
    showMapTypeControl = false,
    showZoomControl = true,
    showFullscreenControl = false,
    showScaleControl = false,
    showStreetViewControl = false,
    includeSearchControl = false,
    mapStyle,
    memorizeZoomLevel = false,
    customMapUrl = null,
    clickableOverlays = false,
  } = props;

  const [map, setMap] = useState();
  const [definedArea, setDefinedArea] = useState();
  const [terraDraw, setTerraDraw] = useState();

  const [drawnAreaWidth, setDrawnAreaWidth] = useState('');
  const [drawnAreaHeight, setDrawnAreaHeight] = useState('');
  const [drawnAreaRadius, setDrawnAreaRadius] = useState('');
  const [drawnAreaUnit, setDrawnAreaUnit] = useState('');

  const [showSearchControl, setShowSearchControl] = useState(false);
  const [customMapLayer, setCustomMapLayer] = useState();

  useEffect(() => {
    const existingCustomMapUrl = MapUtils.getCustomMapUrl(customMapLayer);

    if (!map || existingCustomMapUrl === customMapUrl) return;

    // Draw custom map layer on the map
    const newCustomMapLayer = MapUtils.drawCustomMapLayer(map, customMapUrl, customMapLayer);
    setCustomMapLayer(newCustomMapLayer);

    // Set new map bounds so both custom map and defined area layers are visible
    if (definedArea || newCustomMapLayer) MapUtils.setMapBounds(definedArea, newCustomMapLayer, map);

    // Each time custom map URL changes enable or disable annotate area draging
    MapUtils.setDefinedAreaInteractivity(map, definedArea, customMapUrl);
  }, [map, customMapUrl, customMapLayer, definedArea]);

  // Handle event triggered by user adding new overlay or user editing already existing overlay
  const handleOverlayEvents = useCallback(
    (area, shapeType) => {
      if (!area) return;

      let circle = null;
      let rectangle = null;
      let polygon = null;
      let point = null;

      switch (shapeType) {
        case shapeTypes.CIRCLE: {
          circle = MapUtils.prepareCircleData(area);
          MapUtils.determineCircleDrawnAreaInfo(area, setDrawnAreaRadius, setDrawnAreaUnit);
          break;
        }
        case shapeTypes.RECTANGLE: {
          const rectangleBounds = area.getBounds();

          rectangle = MapUtils.prepareRectangleData(rectangleBounds);
          MapUtils.determinePolygonDrawnAreaInfo(
            rectangleBounds,
            setDrawnAreaWidth,
            setDrawnAreaHeight,
            setDrawnAreaUnit,
          );
          break;
        }
        case shapeTypes.POLYGON: {
          polygon = MapUtils.preparePolygonData(area);

          const polygonBounds = new google.maps.LatLngBounds();
          polygon.path.forEach((coord) => {
            polygonBounds.extend(coord);
          });

          MapUtils.determinePolygonDrawnAreaInfo(
            polygonBounds,
            setDrawnAreaWidth,
            setDrawnAreaHeight,
            setDrawnAreaUnit,
          );
          break;
        }
        case shapeTypes.MARKER: {
          point = MapUtils.preparePointData(area);
          break;
        }
        default:
          break;
      }

      const newMapData = {};
      newMapData.circle = circle;
      newMapData.rectangle = rectangle;
      newMapData.polygon = polygon;
      newMapData.point = point;
      newMapData.shape = shapeType;

      setMapData(newMapData);
    },
    [setMapData],
  );

  useEffect(() => {
    // Create map and drawing tool instances and draw defined area overlay
    if (!map && mapData) {
      // Set up map instance
      const mapInstance = MapUtils.setUpMapInstance(
        id,
        mapOptions,
        mapData,
        mapZoom,
        mapType,
        mapStyle,
        showMapTypeControl,
        showZoomControl,
        showFullscreenControl,
        showScaleControl,
        showStreetViewControl,
      );
      setMap(mapInstance);

      // Set up Terra Draw (replaces the removed google.maps DrawingManager) for shape creation.
      // When a shape finishes drawing we convert its GeoJSON to mapData and hand off to the native
      // overlay render/edit path below — keeping the persisted format and edit behavior unchanged.
      const terraDrawInstance = MapUtils.setUpTerraDraw(mapInstance);
      MapUtils.attachTerraDrawHandlers(terraDrawInstance, (feature) => {
        const newMapData = MapUtils.terraFeatureToMapData(feature);
        newMapData.mapTypeId = mapInstance.getMapTypeId();

        // Remove the transient Terra Draw feature and return to idle.
        terraDrawInstance.clear();
        terraDrawInstance.setMode('static');

        setMapData(newMapData);

        // Render the editable native overlay and wire it up exactly like an existing saved shape.
        const drawnArea = MapUtils.drawDefinedAreaOverlay(mapInstance, newMapData, clickableOverlays);
        if (drawnArea && drawnArea.shape) {
          setDefinedArea({ overlay: drawnArea.shape, type: newMapData.shape, bounds: drawnArea.bounds });
        }
      });
      setTerraDraw(terraDrawInstance);

      // Set up search control if required
      if (includeSearchControl) MapUtils.setUpSearchControl(id, mapInstance);

      // If area is defined draw overlay
      const drawnArea = MapUtils.drawDefinedAreaOverlay(mapInstance, mapData, clickableOverlays);

      if (drawnArea) {
        // After adding defined area overlay adjust map position and zoom level
        if (drawnArea.bounds)
          MapUtils.setMapPositionAndZoomLevel(mapInstance, drawnArea.bounds, mapZoom, memorizeZoomLevel);

        if (drawnArea.shape)
          setDefinedArea({ overlay: drawnArea.shape, type: mapData.shape, bounds: drawnArea.bounds });
      }

      // Set up event listeners for map type change and zoom change
      MapUtils.attachMapEventHandlers(mapInstance, memorizeZoomLevel, setMapZoom, setMapType);
    }
  }, [
    id,
    map,
    mapData,
    showMapTypeControl,
    includeSearchControl,
    showZoomControl,
    showFullscreenControl,
    showScaleControl,
    showStreetViewControl,
    allowedDrawTools,
    mapStyle,
    memorizeZoomLevel,
    clickableOverlays,
    handleOverlayEvents,
    mapZoom,
    mapType,
    setMapZoom,
    setMapType,
    setMapData,
  ]);

  useEffect(() => {
    if (definedArea) {
      // Set event listeners for newly added overlay
      MapUtils.attachDefinedAreaEventHandlers(definedArea, handleOverlayEvents);

      // Calculate defined area dimensions
      switch (definedArea.type) {
        case shapeTypes.CIRCLE: {
          MapUtils.determineCircleDrawnAreaInfo(definedArea.overlay, setDrawnAreaRadius, setDrawnAreaUnit);
          break;
        }
        case shapeTypes.POLYGON: {
          const polygonBounds = new google.maps.LatLngBounds();
          definedArea.overlay.getPath().forEach((coord) => {
            polygonBounds.extend(coord);
          });
          MapUtils.determinePolygonDrawnAreaInfo(
            polygonBounds,
            setDrawnAreaWidth,
            setDrawnAreaHeight,
            setDrawnAreaUnit,
          );
          break;
        }
        case shapeTypes.RECTANGLE: {
          MapUtils.determinePolygonDrawnAreaInfo(
            definedArea.overlay.getBounds(),
            setDrawnAreaWidth,
            setDrawnAreaHeight,
            setDrawnAreaUnit,
          );
          break;
        }
        default:
          break;
      }

      setShowSearchControl(false);
    } else {
      // Clear overlay info after overlay is removed
      setDrawnAreaRadius('');
      setDrawnAreaUnit('');
      setDrawnAreaHeight('');
      setDrawnAreaWidth('');

      setShowSearchControl(true);
    }
  }, [definedArea, handleOverlayEvents]);

  function clearShape() {
    // Detach removed overlay from map, clear all event listeners on removed overlay.
    // Marker overlays are AdvancedMarkerElement (detach via `.map = null`, no setMap); shapes use setMap(null).
    google.maps.event.clearInstanceListeners(definedArea.overlay);
    if (definedArea.type === shapeTypes.MARKER) {
      definedArea.overlay.map = null;
    } else {
      definedArea.overlay.setMap(null);
    }
    setDefinedArea(null);

    // The draw-tool control reappears automatically once definedArea is cleared.
    if (terraDraw) terraDraw.setMode('static');

    // Clear defined area object
    const newMapData = {};
    newMapData.shape = '';
    newMapData.circle = null;
    newMapData.rectangle = null;
    newMapData.polygon = null;
    newMapData.point = null;
    newMapData.mapTypeId = mapData.mapTypeId;
    setMapData(newMapData);
  }

  // Activate the Terra Draw mode for the chosen tool (our shapeTypes value -> Terra Draw mode name).
  // terraDraw starts on the map's first 'idle'; ignore clicks in the brief window before it's enabled.
  function selectDrawTool(shapeType) {
    if (!terraDraw || !terraDraw.enabled) return;
    const mode = MapUtils.shapeTypeToTerraMode(shapeType);
    if (mode) terraDraw.setMode(mode);
  }

  return (
    <div className='c-field l-form__item '>
      <label className='c-field__label'>{label}</label>
      <div className='c-field__group'>
        <div className='o-map' style={{ position: 'relative' }}>
          {includeSearchControl && <MapSearchBox id={id} show={showSearchControl} />}
          <div id={`map-${id}`} className='o-map__content' />
          {!definedArea && terraDraw && (
            <div className='o-map__draw-tools' style={DRAW_TOOLS_STYLE}>
              {allowedDrawTools.map((tool) => (
                <DrawToolButton
                  key={tool}
                  tool={tool}
                  label={DRAW_TOOL_LABELS[tool] || tool}
                  onSelect={selectDrawTool}
                />
              ))}
            </div>
          )}
          <p className='o-map__info' />
        </div>

        <MapOverlayInfo
          mapDescription={mapDescription}
          drawnShape={definedArea}
          drawnAreaRadius={drawnAreaRadius}
          drawnAreaUnit={drawnAreaUnit}
          drawnAreaWidth={drawnAreaWidth}
          drawnAreaHeight={drawnAreaHeight}
          clearMapText={clearMapText}
          clearShape={clearShape}
        />
      </div>
    </div>
  );
}

MapComponent.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  mapData: PropTypes.object,
  setMapData: PropTypes.func,
  mapZoom: PropTypes.number,
  setMapZoom: PropTypes.func,
  mapType: PropTypes.string,
  setMapType: PropTypes.func,
  mapDescription: PropTypes.string.isRequired,
  clearMapText: PropTypes.string.isRequired,
  allowedDrawTools: PropTypes.array.isRequired,
  showMapTypeControl: PropTypes.bool,
  showZoomControl: PropTypes.bool,
  showFullscreenControl: PropTypes.bool,
  showScaleControl: PropTypes.bool,
  showStreetViewControl: PropTypes.bool,
  includeSearchControl: PropTypes.bool,
  mapStyle: PropTypes.array,
  memorizeZoomLevel: PropTypes.bool,
  customMapUrl: PropTypes.string,
  clickableOverlays: PropTypes.bool,
};

export default MapComponent;
